// Types & mock data for the round-robin lead distribution board.

export type LeadSourceId =
  | 'lendingtree'
  | 'bankrate'
  | 'bankingbridge'
  | 'zillow'
  | 'organic'

export interface LeadSource {
  id: LeadSourceId
  name: string
  /** High-value pullable sources can be reclaimed if the LO doesn't act fast enough. */
  pullable: boolean
  /** Relative tier used for subtle visual weighting. */
  tier: 'high' | 'mid' | 'volume'
}

export interface SourceCapacity {
  sourceId: LeadSourceId
  assigned: number
  cap: number
  /** Whether this LO is eligible for the source at all. */
  eligible: boolean
}

export const US_STATES: string[] = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]

export interface LoanOfficer {
  id: string
  /** NMLS identifier shown in the licenses modal. */
  nmls: string
  name: string
  email: string
  /** Calculated from active daily time blocks and any administrator override. */
  ready: boolean
  /** Explicit administrator state; undefined keeps this officer on automatic scheduling. */
  manualReady?: boolean
  /** Multiple daily availability windows, managed by an administrator. */
  timeBlocks: DailyTimeBlock[]
  /** State codes the LO is licensed in. */
  licensedStates: string[]
  /** null = no lead assigned yet today. */
  lastAssignedAt: string | null
  capacities: SourceCapacity[]
}

export interface DailyTimeBlock {
  id: string
  /** Weekdays this block repeats on. 0 = Sunday, matching Date.prototype.getDay(). */
  days: number[]
  start: string
  end: string
}

export const LEAD_SOURCES: LeadSource[] = [
  { id: 'bankrate', name: 'Bankrate', pullable: true, tier: 'high' },
  { id: 'bankingbridge', name: 'Banking Bridge', pullable: true, tier: 'high' },
  { id: 'zillow', name: 'Zillow', pullable: false, tier: 'mid' },
  { id: 'lendingtree', name: 'LendingTree', pullable: false, tier: 'volume' },
  { id: 'organic', name: 'Organic', pullable: false, tier: 'volume' },
]

const now = new Date()
const minsAgo = (m: number) =>
  new Date(now.getTime() - m * 60_000).toISOString()

function cap(
  sourceId: LeadSourceId,
  assigned: number,
  capVal: number,
  eligible = true,
): SourceCapacity {
  return { sourceId, assigned, cap: capVal, eligible }
}

export const LOAN_OFFICERS: LoanOfficer[] = [
  {
    id: 'lo-1',
    nmls: '1381594',
    name: 'Maria Alvarez',
    email: 'maria.alvarez@rchomeloans.com',
    ready: false,
    timeBlocks: [
      { id: 'maria-morning', days: [1, 2, 3, 4, 5], start: '08:00', end: '12:00' },
      { id: 'maria-afternoon', days: [1, 3, 5], start: '13:00', end: '17:30' },
    ],
    licensedStates: ['CA', 'TX', 'AZ', 'NV'],
    lastAssignedAt: minsAgo(6),
    capacities: [cap('bankrate', 2, 4), cap('bankingbridge', 1, 3), cap('zillow', 3, 6), cap('lendingtree', 8, 12), cap('organic', 1, 5)],
  },
  {
    id: 'lo-2',
    nmls: '1204877',
    name: 'James Okafor',
    email: 'james.okafor@rchomeloans.com',
    ready: false,
    timeBlocks: [
      { id: 'james-standard', days: [1, 2, 3, 4, 5], start: '09:00', end: '18:00' },
      { id: 'james-saturday', days: [6], start: '10:00', end: '14:00' },
    ],
    licensedStates: ['FL', 'GA', 'NC', 'SC'],
    lastAssignedAt: minsAgo(42),
    capacities: [cap('bankrate', 4, 4), cap('bankingbridge', 2, 3), cap('zillow', 1, 6), cap('lendingtree', 5, 12), cap('organic', 0, 5, false)],
  },
  {
    id: 'lo-3',
    nmls: '998214',
    name: 'Priya Nair',
    email: 'priya.nair@rchomeloans.com',
    ready: false,
    timeBlocks: [{ id: 'priya-standard', days: [1, 2, 3, 4, 5], start: '07:30', end: '16:00' }],
    licensedStates: ['NY', 'NJ', 'CT', 'MA', 'PA'],
    lastAssignedAt: minsAgo(118),
    capacities: [cap('bankrate', 0, 3), cap('bankingbridge', 0, 2, false), cap('zillow', 2, 5), cap('lendingtree', 3, 10), cap('organic', 2, 4)],
  },
  {
    id: 'lo-4',
    nmls: '1567390',
    name: 'Derek Chen',
    email: 'derek.chen@rchomeloans.com',
    ready: false,
    timeBlocks: [
      { id: 'derek-morning', days: [2, 4], start: '08:30', end: '11:30' },
      { id: 'derek-afternoon', days: [1, 2, 3, 4, 5], start: '14:00', end: '18:00' },
    ],
    licensedStates: ['WA', 'OR', 'ID', 'CA'],
    lastAssignedAt: null,
    capacities: [cap('bankrate', 1, 5), cap('bankingbridge', 1, 4), cap('zillow', 0, 6), cap('lendingtree', 2, 12), cap('organic', 0, 5)],
  },
  {
    id: 'lo-5',
    nmls: '1122045',
    name: 'Sofia Rossi',
    email: 'sofia.rossi@rchomeloans.com',
    ready: false,
    timeBlocks: [{ id: 'sofia-standard', days: [1, 2, 3, 4, 5, 6], start: '10:00', end: '19:00' }],
    licensedStates: ['CO', 'UT', 'AZ'],
    lastAssignedAt: minsAgo(15),
    capacities: [cap('bankrate', 3, 4), cap('bankingbridge', 3, 3), cap('zillow', 4, 6), cap('lendingtree', 11, 12), cap('organic', 3, 5)],
  },
  {
    id: 'lo-6',
    nmls: '1849302',
    name: 'Tobias Wright',
    email: 'tobias.wright@rchomeloans.com',
    ready: false,
    timeBlocks: [],
    licensedStates: ['TX', 'OK', 'LA'],
    lastAssignedAt: minsAgo(320),
    capacities: [cap('bankrate', 0, 4), cap('bankingbridge', 0, 3), cap('zillow', 0, 6), cap('lendingtree', 0, 12), cap('organic', 0, 5)],
  },
  {
    id: 'lo-7',
    nmls: '1043778',
    name: 'Hannah Kim',
    email: 'hannah.kim@rchomeloans.com',
    ready: false,
    timeBlocks: [{ id: 'hannah-standard', days: [1, 2, 3, 4, 5], start: '09:00', end: '17:00' }],
    licensedStates: ['IL', 'WI', 'MN', 'IN'],
    lastAssignedAt: minsAgo(410),
    capacities: [cap('bankrate', 0, 3), cap('bankingbridge', 0, 2), cap('zillow', 0, 5), cap('lendingtree', 0, 10), cap('organic', 0, 4)],
  },
]
